import { InAppKeyboardLayoutId } from '~/in-app-keyboard/types/inAppKeyboard.types'
import {
  Activity as IActivity,
  ActivityQuestionType,
  bootcampOverviewModule,
  FillInTheBlankQuestion,
  LevelActivities,
  PageLevel,
  MultipleChoiceQuestion,
  VccModuleData,
  CircleCombinationsQuestion,
  MatchingPairsQuestion,
  NumberLineQuestion,
  MeasureTheItemQuestion,
} from '~/types/bootcamp'

import { CATEGORY_PATHS, GRADE_QUERY_PARAM } from '~/bootcamp/constants/bootcamp.const'

import { FileTopic } from '~/types/theory'
import { AppLanguage } from '~/types/locale'
import { BadgeImage } from '~/shared/types/bootcampBadge.types'
import { TopicId } from '~/dashboard/utils/learningPath.utils'

import { EventInfo } from '~/gamification/types/gamification.types'

import { CategorizationQuestion } from '../Activities/SanityActivities/Categorization/types/Categorization.type'
import { OrderElementsQuestion } from '../Activities/SanityActivities/OrderElements/types/OrderElements.types'

export interface Module {
  _id: string
  name: string
  key: string
  description: unknown[]
}

export interface Section {
  title: string
  modules: Module[]
}

export enum BREAKPOINTS {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
  TABLET = 'tablet',
}

export enum LevelActionType {
  Activity = 'activity',
  Round = 'round',
  How = 'how',
  Why = 'why',
  Definition = 'definition',
  Quiz = 'quiz',
  Encyclopedia = 'encyclopedia',
  AI = 'ai-prompt',
  Bootcamp = 'bootcamp',
}

export interface LevelPageDetailsType {
  valid?: boolean // if false Redirect to 404
  activities?: IActivity[]
  chest?: { funfact?: unknown[]; event?: EventInfo }
  content?: unknown[] // Why/How/Activity Contents
  subjectName?: string
  help?: {
    encyclopediaPath: string // Encyclopedia path
    drillPath: string // Drill path
  }
  levelsPageData?: PageLevel[]
  sectionName?: string // SectionName
  whyShortTitle?: string
  title?: string // Module title
  totalLevels?: number // Total levels
  totalModules?: number // Total Modules
  usefulWords?: Array<{ term: string; definition?: unknown[]; link?: string }> // Useful Words Contents
  metaDescription?: string
  levelActivities?: LevelActivities
  complexityLevel?: number // Overall complexity of a bootcamp
  hasWhyLevel?: boolean
  badgeImage?: BadgeImage
  badgeOutlineImage?: BadgeImage
  noindex?: boolean
  isEnglishOnly?: boolean
  encyclopediaArticle: FileTopic | null
  levelIds?: string[]
}

export type ActivityTypeKey = `activity-type#${number}`
export type DifficultyLevelKey = `difficulty-level#${number}`
export type LevelKey = `${string}-${string}-${string}`

export type Rewards = {
  [key1 in ActivityTypeKey]?: {
    [key2 in DifficultyLevelKey]: {
      availableRewards: number
      collectedRewards: number
    }
  }
}

export interface TimesTableCell {
  x: number // position on x axis
  y: number // position on y axis
}

export enum TimesTableCellStatus {
  Wrong = 'wrong',
  Completed = 'completed',
  Selected = 'selected',
  Default = 'default',
}

export enum BossState {
  READY = 'ready',
  DAMAGE_BOSS = 'damageBoss',
  DAMAGE_PLAYER = 'damagePlayer',
}

export interface CollectedRewards {
  activityType: number
  collected: number
  available: number
}

export interface ICollectedRewards {
  [name: ActivityTypeKey]: {
    [name: DifficultyLevelKey]: {
      collectedRewards: number
    }
  }
}

export interface BootcampUserEvents {
  processedLeafs: string[] | undefined
  collectedRewards: CollectedRewards[] | undefined
  completedIds?: string[] // Array of level ids that are completed
}

export enum BootcampKey {
  MULTIPLICATION = 'multiplication',
}

export interface prerequisite {
  name: string // Title of the Prerequisite
  url?: string // Link of the related doc
}

/**
 * Bootcamp event ID stored in Firestore.
 * @example 'multiplication#1#1#activity#1#1#1#red#2#250#1'
 * @example 'multiplication#1#1#activity#1#2#1#red#3#275#2'
 *
 * Index 0: Bootcamp key
 * Index 1: Module ID
 * Index 2: Level ID
 * Index 3: Level Action
 * Index 4: Activity type
 * Index 5: Difficulty level
 * Index 6: Gem Color
 * Index 7: Gem Count
 * Index 8: Xp Count
 * Index 9: Round
 */
export type BootcampActivityEventId =
  `${BootcampKey}#${string}#${string}#${LevelActionType.Activity}#${number}#${number}#${number}#${string}#${number}#${number}#${number}`

export type BootcampActivityRoundEventId = `${BootcampKey}#${string}#${string}#${LevelActionType.Round}#${number}`

export interface BootcampActivityEvent {
  bootcampKey: BootcampKey
  moduleId: string
  levelId: string
  levelAction: LevelActionType.Activity
  activityType: number
  difficultyLevel: number
  rewardLevel: number
  round: number
}

/**
 * Sanity/RTK Query Argument type for Bootcamp Level/Page data
 */
export interface BootcampLevelArg {
  key: string
  module: string
  level: string
  type: LevelActionType
  language: AppLanguage
}

export interface BootcampThumbnailArg extends BootcampLevelArg {
  action: string
}
/**
 * Bootcamp Activity Page URL Params Type
 */
export interface BootcampPageUrlParamsType {
  action: LevelActionType
  key: string
  level: string
  moduleKey: string
  subtopic: string
  type?: string
  [GRADE_QUERY_PARAM]?: boolean
}

export interface BootcampModuleCard extends Omit<bootcampOverviewModule, 'moduleNumber' | 'levels'> {
  path: string
  moduleColor?: string
  borderColor?: string
  secondaryColor?: string
  moduleKey?: string
  topicsIconData?: { iconKey: string; key: TopicId; href: string; iconAlt: string }
  isSubjectUpcoming?: boolean
  onClick?: () => void
}

export interface BootcampActivitySet {
  activityType: number // Which Activity Values are 1, 2,
  activityWeight: number // Global Difficulty weight
  difficulties: Array<{
    activityName: string
    description?: unknown[] // Activity instruction
    difficultyLevel: number // Which Difficulty Level Values are 1, 2, 3 ...
    keyboardLayout?: InAppKeyboardLayoutId | null
    questionType: ActivityQuestionType // Question type for activity for new sanity questions
    multipleChoiceActivity: MultipleChoiceQuestion[] // multiple choice questions
    fillInTheBlankActivity: FillInTheBlankQuestion[] // fill in the blank questions
    circleCombinationsActivity: CircleCombinationsQuestion[] // fill in the blank questions
    categorizationActivity: CategorizationQuestion[] // drag and drop questions
    matchingPairsActivity: MatchingPairsQuestion[] // connecting pairs questions
    orderElementsActivity: OrderElementsQuestion[] // drag and sort questions
    numberLineActivity: NumberLineQuestion[] // number line questions
    measureTheItemActivity: MeasureTheItemQuestion[] // measure the item questions
  }>
  workLoadLevel: number
}

export interface BootcampBossData {
  level: string // Level Id values are '1', '2', '3'...
  activities: BootcampActivitySet[]
}

export interface BootcampGymPath {
  gymCategory: string
  key: string
}

export interface ActivityMenu {
  type: LevelActionType
  title: string
  level: string | number
  levelTag?: boolean
  index?: number
  onMenuClick?: () => void
  icon?: LevelActionType
}

// Type derived from the values of CATEGORY_PATHS object
export type CategoryPathUrls = (typeof CATEGORY_PATHS)[keyof typeof CATEGORY_PATHS]

export type CategoriesData = Record<
  CategoryPathUrls,
  Array<{
    _id: string
    chests: []
    noOfVideo: number
    noOfActivities: number
    key: string
  }>
>
export type CategoriesDataVideo = Record<CategoryPathUrls, { watchedVideoCount: number }>

export interface SubjectDefaultName {
  defaultName: string
  key: string
  category: string
  categoryLanguageKey: string
  name: string
}

export enum Tag {
  NEW = 'new',
  UPDATED = 'updated',
  COMING_SOON = 'comingSoon',
}

export enum FunFactChestEventType {
  OPEN = 'open',
}

export interface FunFactChestEvent {
  id: string
  viewKey: string
  type: FunFactChestEventType
  time: string
}

export interface VccData {
  name?: string
  module?: VccModuleData
  moduleCount?: number
}
export interface CoursePageHeaderProps {
  path: string
  title: string
  isExpanded: boolean
  type: LevelActionType
  isCompleted: boolean
  isCollapsed: boolean
  onClick: () => void
}

export enum ActivityKey {
  multipleChoiceActivity = 'multipleChoiceActivity',
  fillInTheBlankActivity = 'fillInTheBlankActivity',
  circleCombinationsActivity = 'circleCombinationsActivity',
  categorizationActivity = 'categorizationActivity',
  matchingPairsActivity = 'matchingPairsActivity',
  orderElementsActivity = 'orderElementsActivity',
  numberLineActivity = 'numberLineActivity',
  measureTheItemActivity = 'measureTheItemActivity',
}

export enum HelpToggleType {
  bootcamp,
  homMethods,
}
